import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'
import styled from 'styled-components'
import { DashboardInput } from 'bd-web-components'
import { APIDecoratorWithBaseURI } from '../../Service/api'
import { Dot, SearchWrap, SearchSpinner } from './styles'
import { useClickOutside } from '../../hooks/use-click-outside'

export interface PickedEvent {
  id: number
  nome: string
  cliente?: string
  start_at?: string
  ingressos?: number
}

interface Props {
  label: string
  color: string
  values: PickedEvent[]
  onChange: (evs: PickedEvent[]) => void
  /** limita a busca a um produtor específico (eventos.ClienteId). */
  clienteId?: number | null
  /** rótulo de uma opção "Todos" fixa no topo do dropdown (ex.: "Todos os Eventos"). */
  allOptionLabel?: string
  /** a opção "Todos" está ativa (marca a linha e o botão). */
  allSelected?: boolean
  /** callback ao clicar na opção "Todos". */
  onSelectAll?: () => void
}

const Wrap = styled.div`
  position: relative;
`
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
`
const Chip = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  font-size: 12px;
  font-weight: 560;
  color: #212121;
  background: #f2f4f7;
  border: 1px solid #eaecf0;
  border-left: 3px solid ${(p) => p.$color};
  border-radius: 8px;
  padding: 4px 6px 4px 8px;
  b {
    font-weight: 600;
    white-space: normal;
    overflow-wrap: anywhere;
    max-width: 300px;
    line-height: 1.25;
  }
  button {
    border: 0;
    background: transparent;
    color: #B0B0B0;
    cursor: pointer;
    font-size: 13px;
    line-height: 1;
    padding: 0 2px;
  }
  button:hover {
    color: #b42318;
  }
`
const Selected = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #eaecf0;
  background: #F9F9F9;
  border-radius: 11px;
  padding: 9px 12px;
  cursor: pointer;
  text-align: left;
  &:hover {
    border-color: #B0B0B0;
  }
`
const SelName = styled.div`
  min-width: 0;
  b {
    display: block;
    font-weight: 600;
    font-size: 13.5px;
    color: #212121;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  span {
    font-size: 11.5px;
    color: #B0B0B0;
  }
`
const Chevron = styled.span`
  margin-left: auto;
  color: #B0B0B0;
`
const Panel = styled.div`
  position: absolute;
  z-index: 40;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  box-shadow: 0 12px 32px -12px rgba(16, 24, 40, 0.24);
  padding: 10px;
`
const List = styled.ul`
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  max-height: 260px;
  overflow-y: auto;
`
const Row = styled.li`
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
  b {
    display: block;
    font-size: 13px;
    color: #212121;
    font-weight: 560;
  }
  span {
    font-size: 11.5px;
    color: #B0B0B0;
  }
  &:hover {
    background: #F9F9F9;
  }
`
const Hint = styled.div`
  font-size: 12px;
  color: #B0B0B0;
  padding: 10px;
  text-align: center;
`
const PanelHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`
const ActiveToggle = styled.label<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: #555555;
  white-space: nowrap;
  cursor: pointer;
  input {
    accent-color: ${(p) => p.$color};
    cursor: pointer;
  }
`

/** 'YYYY-MM-DD HH:mm:ss' -> 'DD/MM/YYYY' (com ano, p/ desambiguar nomes repetidos). */
const fmtEventDate = (s?: string): string => {
  if (!s) return ''
  const d = s.slice(0, 10).split('-')
  return d.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : ''
}
const fmtInt = (n: number) => Math.round(n).toLocaleString('pt-BR')
/** Linha secundária do picker: produtor · data · volume de ingressos. */
const eventCaption = (ev: { cliente?: string; start_at?: string; ingressos?: number }): string =>
  [
    ev.cliente,
    fmtEventDate(ev.start_at),
    ev.ingressos != null ? `${fmtInt(ev.ingressos)} ingressos` : '',
  ]
    .filter(Boolean)
    .join(' · ')

const EventPicker: FC<Props> = ({
  label,
  color,
  values,
  onChange,
  clienteId,
  allOptionLabel,
  allSelected = false,
  onSelectAll,
}) => {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  useClickOutside(rootRef, () => setOpen(false), open)
  const [name, setName] = useState('')
  const [debouncedName, setDebouncedName] = useState('')
  const [onlyActive, setOnlyActive] = useState(false)

  // debounce da busca (300ms) — evita 1 request por tecla.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(name), 300)
    return () => clearTimeout(t)
  }, [name])

  // Busca por nome em TODAS as datas (futuros + passados), até 50 resultados —
  // produtores têm muitos eventos homônimos (ex.: "Tardezinha"). O toggle
  // "Somente ativos" restringe por eventos.Ativo=1, sem trava de data; e o
  // filtro de produtor (clienteId) limita a busca a um produtor específico.
  const parts = ['page=1', 'limit=50']
  if (onlyActive) parts.push('active=1')
  if (clienteId) parts.push(`clienteId=${clienteId}`)
  const url = open
    ? `producer/analytics/events?${parts.join('&')}&name=${encodeURIComponent(debouncedName)}`
    : null
  const { data, isLoading, isValidating } = useSWR<any>(url, (u: string) => APIDecoratorWithBaseURI().get(u))

  const events: PickedEvent[] = useMemo(() => {
    const arr = data?.data?.data ?? data?.data?.body?.data ?? []
    return Array.isArray(arr) ? arr : []
  }, [data])

  const total: number | undefined = data?.data?.total ?? data?.data?.body?.total

  const selectedIds = useMemo(() => new Set(values.map((v) => v.id)), [values])

  // clicar num evento da lista adiciona/remove do grupo (multi-seleção)
  const toggle = (ev: PickedEvent) => {
    if (selectedIds.has(ev.id)) onChange(values.filter((v) => v.id !== ev.id))
    else onChange([...values, ev])
  }
  const removeChip = (id: number) => onChange(values.filter((v) => v.id !== id))

  return (
    <Wrap ref={rootRef}>
      {values.length > 0 && (
        <Chips>
          {values.map((v) => (
            <Chip key={v.id} $color={color} title={v.nome}>
              <b>{v.nome}</b>
              <button type="button" onClick={() => removeChip(v.id)} aria-label="Remover">
                ✕
              </button>
            </Chip>
          ))}
        </Chips>
      )}
      <Selected type="button" onClick={() => setOpen((o) => !o)}>
        <Dot $color={color} />
        <SelName>
          {allSelected && allOptionLabel ? (
            <>
              <b>{allOptionLabel}</b>
              <span>todos incluídos</span>
            </>
          ) : (
            <>
              <b style={{ color: '#B0B0B0', fontWeight: 500 }}>
                {values.length
                  ? `Adicionar outro evento a ${label.toLowerCase()}…`
                  : `Selecionar ${label.toLowerCase()}…`}
              </b>
              <span>
                {values.length
                  ? `${values.length} evento(s) somados neste lado`
                  : 'clique para buscar (some vários = 1 evento lógico)'}
              </span>
            </>
          )}
        </SelName>
        <Chevron>⌄</Chevron>
      </Selected>

      {open && (
        <Panel>
          <PanelHead>
            <div style={{ flex: 1, minWidth: 0 }}>
              <SearchWrap>
                <DashboardInput
                  type={'input'}
                  name={`search-${label}`}
                  placeholder={'Buscar evento pelo nome'}
                  value={name}
                  onChange={(e: any) => setName(e.target.value)}
                />
                {isValidating && <SearchSpinner />}
              </SearchWrap>
            </div>
            <ActiveToggle $color={color}>
              <input
                type="checkbox"
                checked={onlyActive}
                onChange={(e) => setOnlyActive(e.target.checked)}
              />
              Somente ativos
            </ActiveToggle>
          </PanelHead>
          {allOptionLabel && onSelectAll && (
            <List style={{ marginTop: 0, borderBottom: '1px solid #f2f4f7', paddingBottom: 6, marginBottom: 4 }}>
              <Row onClick={onSelectAll} style={allSelected ? { background: '#f0f3ff' } : undefined}>
                <b>
                  <span style={{ color, marginRight: 6 }}>{allSelected ? '✓' : '+'}</span>
                  {allOptionLabel}
                </b>
              </Row>
            </List>
          )}
          {isLoading ? (
            <Hint>Buscando…</Hint>
          ) : events.length === 0 ? (
            <Hint>Nenhum evento encontrado.</Hint>
          ) : (
            <>
              <List>
                {events.map((ev) => {
                  const on = selectedIds.has(ev.id)
                  return (
                    <Row key={ev.id} onClick={() => toggle(ev)} style={on ? { background: '#f0f3ff' } : undefined}>
                      <b>
                        <span style={{ color, marginRight: 6 }}>{on ? '✓' : '+'}</span>
                        {ev.nome}
                      </b>
                      <span>{eventCaption(ev)}</span>
                    </Row>
                  )
                })}
              </List>
              {total !== undefined && total > events.length && (
                <Hint>
                  Mostrando {events.length} de {total} — refine a busca pelo nome.
                </Hint>
              )}
            </>
          )}
        </Panel>
      )}
    </Wrap>
  )
}

export default EventPicker
